import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../provider/authProvider';
import { Login as LoginAPI } from '../api/auth';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { setToken } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await LoginAPI(email, password);

      if (result.token) {
        setToken(result.token);
        localStorage.setItem('user', JSON.stringify(result.user));
        navigate('/dashboard');
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (err) {
      setError('Connection error. Please check if FixEngine is running.');
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: '#0f172a',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    card: {
      background: '#1e293b',
      padding: '32px',
      borderRadius: '8px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      width: '384px',
      border: '1px solid #334155'
    },
    header: {
      marginBottom: '24px',
      textAlign: 'center' as const
    },
    logo: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#3b82f6',
      marginBottom: '8px'
    },
    title: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#f1f5f9'
    },
    subtitle: {
      fontSize: '14px',
      color: '#94a3b8',
      marginTop: '4px'
    },
    formGroup: {
      marginBottom: '16px'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '500',
      marginBottom: '8px',
      color: '#cbd5e1'
    },
    input: {
      width: '100%',
      padding: '8px 12px',
      background: '#334155',
      border: '1px solid #475569',
      borderRadius: '4px',
      color: '#f1f5f9',
      fontSize: '14px',
      outline: 'none',
      transition: 'all 0.2s'
    },
    error: {
      marginBottom: '16px',
      padding: '12px',
      background: 'rgba(127, 29, 29, 0.5)',
      border: '1px solid #991b1b',
      color: '#fecaca',
      borderRadius: '4px',
      fontSize: '14px'
    },
    button: {
      width: '100%',
      background: '#2563eb',
      color: 'white',
      padding: '8px',
      borderRadius: '4px',
      border: 'none',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'background 0.2s'
    },
    buttonDisabled: {
      background: '#475569',
      cursor: 'not-allowed'
    },
    footer: {
      marginTop: '16px',
      textAlign: 'center' as const,
      fontSize: '12px',
      color: '#64748b'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.logo}>◉→◉→◉</div>
          <div style={styles.title}>QuantXFeed Admin</div>
          <div style={styles.subtitle}>DataFeed Management Portal</div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              required
              disabled={loading}
            />
          </div>

          <div style={{...styles.formGroup, marginBottom: '24px'}}>
            <label style={styles.label}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              required
              disabled={loading}
            />
          </div>

          {error && (
            <div style={styles.error}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{...styles.button, ...(loading ? styles.buttonDisabled : {})}}
            onMouseEnter={(e) => !loading && (e.currentTarget.style.background = '#1d4ed8')}
            onMouseLeave={(e) => !loading && (e.currentTarget.style.background = '#2563eb')}
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <div style={styles.footer}>
          Admin privileges required
        </div>
      </div>
    </div>
  );
}
